import React from "react";
import { Table, Tag, Popconfirm, Badge, Tooltip } from "antd";
import type { ColumnType } from "antd/es/table";
import { Trash2, Pencil, CheckCircle, XCircle } from "lucide-react";
import dayjs from "dayjs";
import MyPagination from "../../components/Pagination";
import { TCustomFieldDefinition, TCustomFieldType } from "../../../types/customFields";
import { QData } from "../../../types/basic-type-list";

interface CustomFieldsTableProps {
  data: TCustomFieldDefinition[];
  loading: boolean;
  total: number;
  qData: QData;
  companies: any[];
  onChange: (newQData: QData) => void;
  onEdit: (row: TCustomFieldDefinition) => void;
  onDelete: (id: number) => void;
}

const TYPE_CFG: Record<TCustomFieldType, { color: string; label: string }> = {
  text:        { color: "blue",    label: "Text" },
  number:      { color: "purple",  label: "Number" },
  date:        { color: "cyan",    label: "Date" },
  boolean:     { color: "gold",    label: "Boolean" },
  select:      { color: "green",   label: "Select" },
  multiselect: { color: "volcano", label: "Multi-select" },
  file:        { color: "magenta", label: "File" },
};

const CustomFieldsTable: React.FC<CustomFieldsTableProps> = ({
  data,
  loading,
  total,
  qData,
  companies,
  onChange,
  onEdit,
  onDelete,
}) => {
  const columns: ColumnType<TCustomFieldDefinition>[] = [
    {
      title: "Field Label",
      dataIndex: "field_label",
      key: "field_label",
      render: (v: string, row) => (
        <div>
          <div className="font-medium text-slate-800 text-sm">{v}</div>
          <div className="text-xs text-slate-400 font-mono">{row.field_key}</div>
        </div>
      ),
    },
    {
      title: "Company",
      dataIndex: "company_id",
      key: "company_id",
      render: (v: number) => {
        const c = companies.find((comp) => comp.id === v);
        return (
          <span className="text-slate-600 text-sm">
            {c ? `${c.name} (#${v})` : `#${v}`}
          </span>
        );
      },
    },
    {
      title: "Entity Type",
      dataIndex: "entity_type",
      key: "entity_type",
      render: (v: string) => (
        <Tag color="geekblue" className="capitalize">
          {v}
        </Tag>
      ),
    },
    {
      title: "Field Type",
      dataIndex: "field_type",
      key: "field_type",
      render: (v: TCustomFieldType) => (
        <Tag color={TYPE_CFG[v]?.color ?? "default"}>
          {TYPE_CFG[v]?.label ?? v}
        </Tag>
      ),
    },
    {
      title: "Required",
      dataIndex: "is_required",
      key: "is_required",
      align: "center",
      render: (v: boolean | number) =>
        v ? (
          <CheckCircle size={16} className="text-green-500 mx-auto" />
        ) : (
          <XCircle size={16} className="text-slate-300 mx-auto" />
        ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (v: boolean | number) => (
        <Badge
          status={v ? "success" : "default"}
          text={v ? "Active" : "Inactive"}
        />
      ),
    },
    {
      title: "Values Used",
      dataIndex: "value_count",
      key: "value_count",
      align: "center",
      render: (v: any) => (
        <span className="text-slate-600 font-medium">{Number(v) || 0}</span>
      ),
    },
    {
      title: "Sort",
      dataIndex: "sort_order",
      key: "sort_order",
      align: "center",
      render: (v: number) => (
        <span className="text-slate-400 text-xs">{v}</span>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (v: string) => (
        <span className="text-slate-400 text-xs whitespace-nowrap">
          {dayjs(v).format("DD MMM YY")}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, row) => (
        <div className="flex items-center justify-end gap-1.5">
          <Tooltip title="Edit">
            <button
              onClick={() => onEdit(row)}
              className="w-7 h-7 bg-slate-50 rounded-lg flex items-center justify-center hover:bg-violet-50 transition-colors"
            >
              <Pencil size={13} className="text-slate-500 hover:text-violet-600" />
            </button>
          </Tooltip>
          <Popconfirm
            title="Delete this custom field?"
            description="All values linked to this field will also be removed."
            okText="Delete"
            okType="danger"
            onConfirm={() => onDelete(row.id)}
          >
            <Tooltip title="Delete">
              <button className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center hover:bg-red-100 transition-colors">
                <Trash2 size={13} className="text-red-500" />
              </button>
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey={(row) => row.id.toString()}
      pagination={false}
      loading={loading}
      scroll={{ x: "max-content" }}
      footer={() => (
        <MyPagination
          qData={qData}
          total={total}
          onChange={onChange}
        />
      )}
    />
  );
};

export default CustomFieldsTable;
